import React, { useEffect, useState } from "react";

import { LoadingScreen } from "@administrate/piston-ux";
import * as Sentry from "@sentry/browser";
import { dedupeIntegration } from "@sentry/integrations";
import { ApolloError } from "apollo-boost";
import { throwIfNotOK } from "../fetchHelpers";
import { asDiagnostic } from "../utils/errorHelpers";
import { GlobalStoreProvider } from "./GlobalStore";
import { Config } from "./config";

type Environment = "development" | "staging" | "production" | "unknown";

const getEnvironmentFromApiUrl = (apiUrl: string): Environment => {
  if (apiUrl.includes(".devadministratehq.com")) {
    return "development";
  }
  if (apiUrl.includes(".stagingadministratehq.com")) {
    return "staging";
  }
  if (apiUrl.includes(".administratehq.com")) {
    return "production";
  }
  return "unknown";
};

export const loadConfig = async (): Promise<Config> =>
  fetch("/config")
    .then(throwIfNotOK)
    .then((result) => result.json());

const AVAILABILITY_PROBE_MESSAGE = "aev-probe";

const EMBEDDED_PLAYER_PATH = "/players/vulcanV2Player/";
// Third-party scripts (the Wistia player among them) served from the
// portal's own external-assets path, throwing inside themselves
const EXTERNAL_ASSET_PATH = "/assets/external/";
// The login client, which reads session storage the browser may refuse
const AUTH_CLIENT_PATH = "auth0-spa-js";

// A frame belonging to a script inlined into the page rather than a bundle:
// its filename is the document's own URL or path, or nothing at all
const isInlinePageFrame = (
  frame: { filename?: string },
  pageUrl: string | undefined,
) => {
  if (!frame.filename || frame.filename === "<anonymous>") {
    return true;
  }
  if (!pageUrl) {
    return false;
  }
  try {
    const page = new URL(pageUrl);
    return (
      frame.filename === pageUrl ||
      frame.filename === page.pathname ||
      frame.filename === `${page.origin}/`
    );
  } catch {
    return false;
  }
};

// Where an exception was actually thrown, which is the innermost frame
const threwIn =
  (path: string) =>
  (exception: { stacktrace?: { frames?: { filename?: string }[] } }) => {
    const frames = exception.stacktrace?.frames ?? [];
    return (
      frames.length > 0 && !!frames[frames.length - 1].filename?.includes(path)
    );
  };

export const beforeSentrySend = (
  event: Sentry.Event,
  hint?: Sentry.EventHint,
): Sentry.Event | null => {
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "Error" &&
        ["Aa", "Ca"].includes(value ?? "") &&
        (!stacktrace?.frames?.length ||
          stacktrace.frames.every((frame) =>
            isInlinePageFrame(frame, event.request?.url),
          )),
    )
  ) {
    // Third-party scripts minify to two-letter error classes and throw from
    // frames that are the page itself rather than any bundle of ours
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "Error" &&
        value?.startsWith(
          "Uncaught EvalError: Evaluating a string as JavaScript violates the following Content Security Policy directive",
        ) &&
        stacktrace?.frames?.length === 1 &&
        stacktrace.frames[0].filename?.startsWith("blob:"),
    )
  ) {
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "ReferenceError" &&
        value === "Can't find variable: zaloJSV2" &&
        stacktrace?.frames?.length === 1 &&
        stacktrace.frames[0].function === "global code",
    )
  ) {
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "XMLHttpRequestProgressEvent" &&
        value ===
          "Event `XMLHttpRequestProgressEvent` (type=error) captured as exception" &&
        stacktrace?.frames?.length === 1,
    )
  ) {
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        ["XMLHttpRequestProgressEvent", "ProgressEvent"].includes(type ?? "") &&
        value?.endsWith("(type=error) captured as promise rejection") &&
        !stacktrace?.frames?.length,
    )
  ) {
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "UnhandledRejection" &&
        value ===
          "Object captured as promise rejection with keys: code, message, stack" &&
        !stacktrace?.frames?.length,
    )
  ) {
    // Third-party scripts reject with a bare error-shaped object
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "TypeError" &&
        value === "NetworkError when attempting to fetch resource." &&
        !stacktrace?.frames?.length,
    )
  ) {
    // Firefox network failures reported without a hint or stack
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        (type === "Error" &&
          value === "AbortError: The user aborted a request." &&
          !stacktrace?.frames?.length) ||
        (type === "AbortError" && value === "signal is aborted without reason"),
    )
  ) {
    // The learner left the page while a request was still going, which the
    // browser reports back to us as a failure with nothing behind it -
    // either as a plain message or as Apollo tearing down its own query
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value, stacktrace }) =>
        type === "RangeError" &&
        value?.replace(/\.$/, "") === "Maximum call stack size exceeded" &&
        stacktrace?.frames?.some(
          ({ filename }) =>
            filename === "undefined" || filename?.includes("/content/"),
        ) &&
        !stacktrace.frames.some((frame) =>
          frame.filename?.includes("lms-ux-assets"),
        ),
    )
  ) {
    // Overflows whose frames are all content documents or anonymous
    // scripts come from uploaded SCORM packages, not the application
    return null;
  }
  if (
    event.exception?.values?.some(
      ({ type, value }) =>
        type === "NotFoundError" &&
        value?.includes("The node to be removed is not a child of this node"),
    )
  ) {
    // Browser extensions and translators mutate the DOM under React
    return null;
  }
  if (
    event.request?.url?.includes("/content/") &&
    event.exception?.values?.some(
      ({ value, stacktrace }) =>
        [
          "[object MediaError]",
          "Object captured as promise rejection with keys: [object has no keys]",
          "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
        ].includes(value ?? "") && !stacktrace?.frames?.length,
    )
  ) {
    return null;
  }
  if (event.message === AVAILABILITY_PROBE_MESSAGE) {
    // Sent by an external availability monitor, not by this application
    return null;
  }
  if (
    event.exception?.values?.some(
      (exception) =>
        exception.type === "SecurityError" &&
        exception.value?.includes("Access is denied for this document") &&
        threwIn(AUTH_CLIENT_PATH)(exception),
    )
  ) {
    // The browser refused the login client the storage it wanted, which
    // its own sign-in screen reports; we cannot grant it from here
    return null;
  }
  if (
    event.exception?.values?.some(threwIn(EMBEDDED_PLAYER_PATH)) ||
    event.exception?.values?.some(threwIn(EXTERNAL_ASSET_PATH))
  ) {
    // The embedded video player or another external script throwing inside
    // itself, most often on teardown; we cannot patch a bundle we do not build
    return null;
  }
  const exception = hint?.originalException;
  if (
    exception instanceof Error &&
    (exception.message.trim() === "401" ||
      exception.message === "Login required" ||
      exception.message === "Unauthorized 401" ||
      exception.message ===
        "Network error: Response not successful: Received status code 401" ||
      exception.message === "GraphQL error: Request to remote URL failed" ||
      [
        "Failed to fetch",
        "Load failed",
        "Network error: Failed to fetch",
        "Network error: Load failed",
        "Network error: NetworkError when attempting to fetch resource.",
        "Timeout when executing 'fetch'",
      ].includes(exception.message))
  ) {
    return null;
  }
  if (
    exception instanceof Error &&
    exception.message.trim() === "400" &&
    event.request?.url?.includes("/login/") &&
    event.breadcrumbs?.some(
      ({ category, data }) => category === "fetch" && data?.status_code === 400,
    )
  ) {
    return null;
  }
  if (
    exception instanceof Error &&
    exception.message ===
      "HTTP error. Unable to fetch https://identity.getadministrate.com/oauth/token" &&
    event.breadcrumbs?.some(
      ({ category, data }) => category === "xhr" && data?.status_code === 400,
    )
  ) {
    return null;
  }
  if (
    ((exception instanceof RangeError &&
      exception.message === "Maximum call stack size exceeded") ||
      event.exception?.values?.some(
        (value) =>
          value.type === "RangeError" &&
          value.value?.replace(/\.$/, "") ===
            "Maximum call stack size exceeded",
      )) &&
    event.request?.url?.includes("/content/") &&
    event.exception?.values?.some((value) =>
      value.stacktrace?.frames?.some((frame) => frame.filename === "undefined"),
    )
  ) {
    return null;
  }
  if (exception instanceof ApolloError) {
    event.fingerprint = [
      exception.networkError ? "Network" : "GraphQL",
      exception.message,
    ];
  }
  return event;
};

export const ConfigLoader: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    loadConfig()
      .then((config) => {
        if (config.ui_sentry_dsn) {
          Sentry.init({
            dsn: config.ui_sentry_dsn,
            denyUrls: [/\/files\/[a-f0-9]{128}/i],
            integrations: [dedupeIntegration()],
            environment: getEnvironmentFromApiUrl(config.graphql_uri),
            beforeSend: beforeSentrySend,
          });
        }

        setConfig(config);
      })
      .catch(
        asDiagnostic(
          "Config",
          document.location.origin + "/config",
          "Failed to Fetch",
        ),
      );
  }, []);

  if (!config) {
    return <LoadingScreen />;
  }

  return <GlobalStoreProvider config={config}>{children}</GlobalStoreProvider>;
};
